import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Calendar, Clock, Save, Check, DollarSign, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const COMMON_TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'India (IST, UTC+5:30)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST, UTC+4)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT, UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST, UTC+9)' },
  { value: 'Asia/Shanghai', label: 'China (CST, UTC+8)' },
  { value: 'Europe/London', label: 'UK (GMT/BST, UTC+0/+1)' },
  { value: 'Europe/Berlin', label: 'Central Europe (CET, UTC+1)' },
  { value: 'America/New_York', label: 'US Eastern (ET, UTC-5)' },
  { value: 'America/Chicago', label: 'US Central (CT, UTC-6)' },
  { value: 'America/Denver', label: 'US Mountain (MT, UTC-7)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (PT, UTC-8)' },
  { value: 'Australia/Sydney', label: 'Australia (AEST, UTC+10)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST, UTC+12)' },
  { value: 'Africa/Lagos', label: 'West Africa (WAT, UTC+1)' },
  { value: 'America/Sao_Paulo', label: 'Brazil (BRT, UTC-3)' },
];

const CURRENCIES = [
  { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee (INR)' },
  { code: 'USD', symbol: '$', name: 'US Dollar (USD)' },
  { code: 'EUR', symbol: '\u20AC', name: 'Euro (EUR)' },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound (GBP)' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham (AED)' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar (SGD)' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar (AUD)' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar (CAD)' },
  { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen (JPY)' },
];

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'hi', name: 'Hindi', native: '\u0939\u093F\u0928\u094D\u0926\u0940' },
  { code: 'ta', name: 'Tamil', native: '\u0BA4\u0BAE\u0BBF\u0BB4\u0BCD' },
  { code: 'te', name: 'Telugu', native: '\u0C24\u0C46\u0C32\u0C41\u0C17\u0C41' },
  { code: 'kn', name: 'Kannada', native: '\u0C95\u0CA8\u0CCD\u0CA8\u0CA1' },
  { code: 'mr', name: 'Marathi', native: '\u092E\u0930\u093E\u0920\u0940' },
  { code: 'bn', name: 'Bengali', native: '\u09AC\u09BE\u0982\u09B2\u09BE' },
  { code: 'gu', name: 'Gujarati', native: '\u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0' },
];

const FY_PRESETS = [
  { label: 'Apr\u2013Mar (India)', start: 4 },
  { label: 'Jan\u2013Dec (Calendar)', start: 1 },
  { label: 'Jul\u2013Jun (Australia)', start: 7 },
  { label: 'Oct\u2013Sep (US Federal)', start: 10 },
];

export default function OrgSettings() {
  const { language, setLanguage, languages: i18nLanguages } = useI18n();
  const [settings, setSettings] = useState({
    financial_year_start: 4,
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    language: 'en',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/org-settings`, { headers: getHeaders() })
      .then(r => {
        if (r.data) setSettings(prev => ({ ...prev, ...r.data }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/org-settings`, settings, { headers: getHeaders() });
      // Apply language change
      if (settings.language !== language) setLanguage(settings.language);
      // Store currency for site-wide use
      localStorage.setItem('org_currency', settings.currency);
      localStorage.setItem('org_currency_symbol', CURRENCIES.find(c => c.code === settings.currency)?.symbol || '\u20B9');
      toast.success('Organization settings saved');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const fyStart = MONTHS.find(m => m.value === settings.financial_year_start);
  const fyEnd = MONTHS.find(m => m.value === (settings.financial_year_start === 1 ? 12 : settings.financial_year_start - 1));
  const selectedCurrency = CURRENCIES.find(c => c.code === settings.currency);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div data-testid="org-settings-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Settings className="inline-block mr-2 text-blue-600" size={28} />Organization Settings
        </h1>
        <p className="text-slate-500 text-sm">Configure your financial year, timezone, currency, and language — applies to all metrics, reports, and filters</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financial Year */}
        <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm" data-testid="fy-section">
          <div className="flex items-center gap-2 mb-5">
            <Calendar size={20} className="text-blue-600" />
            <h2 className="text-lg font-extrabold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Financial Year</h2>
          </div>
          <div className="mb-5">
            <Label className="text-xs font-bold uppercase text-blue-900">Year Starts In</Label>
            <select value={settings.financial_year_start} onChange={e => setSettings({ ...settings, financial_year_start: parseInt(e.target.value) })}
              className="mt-1.5 flex h-10 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-medium" data-testid="fy-month-select">
              {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {FY_PRESETS.map(p => (
              <button key={p.start} onClick={() => setSettings({ ...settings, financial_year_start: p.start })}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${settings.financial_year_start === p.start ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`}
                data-testid={`fy-preset-${p.start}`}>{p.label}</button>
            ))}
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800"><strong>Current FY:</strong> {fyStart?.label} to {fyEnd?.label}</p>
            <p className="text-[10px] text-blue-600 mt-1">All "This Year" filters, annual targets, and yearly reports will use this period.</p>
          </div>
        </div>

        {/* Timezone */}
        <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm" data-testid="tz-section">
          <div className="flex items-center gap-2 mb-5">
            <Clock size={20} className="text-blue-600" />
            <h2 className="text-lg font-extrabold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Timezone</h2>
          </div>
          <div className="mb-5">
            <Label className="text-xs font-bold uppercase text-blue-900">Organization Timezone</Label>
            <select value={settings.timezone} onChange={e => setSettings({ ...settings, timezone: e.target.value })}
              className="mt-1.5 flex h-10 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-medium" data-testid="tz-select">
              {COMMON_TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800"><strong>Selected:</strong> {COMMON_TIMEZONES.find(tz => tz.value === settings.timezone)?.label || settings.timezone}</p>
            <p className="text-[10px] text-blue-600 mt-1">All timestamps, date filters, attendance, and metrics will be computed in this timezone.</p>
          </div>
        </div>

        {/* Currency */}
        <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm" data-testid="currency-section">
          <div className="flex items-center gap-2 mb-5">
            <DollarSign size={20} className="text-blue-600" />
            <h2 className="text-lg font-extrabold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Currency</h2>
          </div>
          <div className="mb-5">
            <Label className="text-xs font-bold uppercase text-blue-900">Default Currency</Label>
            <select value={settings.currency} onChange={e => setSettings({ ...settings, currency: e.target.value })}
              className="mt-1.5 flex h-10 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-medium" data-testid="currency-select">
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {CURRENCIES.slice(0, 5).map(c => (
              <button key={c.code} onClick={() => setSettings({ ...settings, currency: c.code })}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${settings.currency === c.code ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`}
                data-testid={`currency-${c.code}`}>
                {c.symbol} {c.code}
              </button>
            ))}
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800"><strong>Selected:</strong> {selectedCurrency?.symbol} {selectedCurrency?.name}</p>
            <p className="text-[10px] text-blue-600 mt-1">All revenue figures, CTC, billing, and financial data will display in this currency.</p>
          </div>
        </div>

        {/* Language */}
        <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm" data-testid="language-section">
          <div className="flex items-center gap-2 mb-5">
            <Globe size={20} className="text-blue-600" />
            <h2 className="text-lg font-extrabold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Language</h2>
          </div>
          <div className="mb-5">
            <Label className="text-xs font-bold uppercase text-blue-900">Interface Language</Label>
            <select value={settings.language} onChange={e => setSettings({ ...settings, language: e.target.value })}
              className="mt-1.5 flex h-10 w-full rounded-lg border border-blue-200 bg-white px-3 text-sm font-medium" data-testid="language-select">
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.native} ({l.name})</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {LANGUAGES.slice(0, 4).map(l => (
              <button key={l.code} onClick={() => setSettings({ ...settings, language: l.code })}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${settings.language === l.code ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`}
                data-testid={`lang-${l.code}`}>
                {l.native}
              </button>
            ))}
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800"><strong>Selected:</strong> {LANGUAGES.find(l => l.code === settings.language)?.name || 'English'}</p>
            <p className="text-[10px] text-blue-600 mt-1">Navigation labels and UI text will be displayed in this language where translations are available.</p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-org-settings"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-2.5 rounded-lg">
          {saving ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Saving...</>
            : saved ? <><Check size={16} className="mr-2" />Saved!</>
            : <><Save size={16} className="mr-2" />Save Settings</>}
        </Button>
      </div>
    </div>
  );
}
